import { Injectable, signal } from '@angular/core';

export type MobileErrorCategory = 'network' | 'auth' | 'server' | 'client' | 'unknown';

export interface MobileGlobalErrorNotice {
  key: string;
  message: string;
  status?: number;
  category: MobileErrorCategory;
  severity: 'error' | 'warn' | 'info';
}

export interface MobileGlobalErrorVisible extends MobileGlobalErrorNotice {
  id: number;
  createdAt: number;
}

/**
 * Mobile-only error notice channel with throttling and single visible notice.
 * Designed to avoid stacked global toasts during backend instability.
 */
@Injectable({ providedIn: 'root' })
export class MobileErrorUxService {
  readonly activeError = signal<MobileGlobalErrorVisible | null>(null);
  readonly emittedCount = signal(0);
  readonly dedupedCount = signal(0);

  private readonly dedupeWindowMs = 8000;
  private readonly displayMs = 3600;
  private readonly maxQueueSize = 3;

  private readonly dedupeUntilByKey = new Map<string, number>();
  private readonly queue: MobileGlobalErrorVisible[] = [];
  private nextId = 1;
  private dismissTimer: ReturnType<typeof setTimeout> | null = null;

  notify(notice: MobileGlobalErrorNotice): boolean {
    const now = Date.now();
    this.pruneDedupeCache(now);
    const dedupeUntil = this.dedupeUntilByKey.get(notice.key) ?? 0;
    if (dedupeUntil > now) {
      this.dedupedCount.update((n) => n + 1);
      return false;
    }
    this.dedupeUntilByKey.set(notice.key, now + this.dedupeWindowMs);
    const visible: MobileGlobalErrorVisible = {
      ...notice,
      id: this.nextId++,
      createdAt: now,
    };

    if (!this.activeError()) {
      this.show(visible);
      return true;
    }

    if (this.queue.length >= this.maxQueueSize) {
      this.queue.shift();
    }
    this.queue.push(visible);
    this.emittedCount.update((n) => n + 1);
    return true;
  }

  dismissCurrent(): void {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
    this.activeError.set(null);
    const next = this.queue.shift();
    if (next) {
      this.show(next);
    }
  }

  private show(error: MobileGlobalErrorVisible): void {
    this.activeError.set(error);
    this.emittedCount.update((n) => n + 1);
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
    }
    this.dismissTimer = setTimeout(() => this.dismissCurrent(), this.displayMs);
  }

  private pruneDedupeCache(now: number): void {
    for (const [key, until] of this.dedupeUntilByKey.entries()) {
      if (until <= now) {
        this.dedupeUntilByKey.delete(key);
      }
    }
  }
}

